import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import SetPasswordModal from "./SetPasswordModal";
import { EqualMark, Eyebrow, Brand, StepNumber } from "@/components/brand";
import { RoadmapNudge } from "@/components/RoadmapNudge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Strip file extension for display
const stripExt = (name) => name ? name.replace(/\.[^/.]+$/, '') : name;

const YouTubeIcon = () => (
  <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.5 6.19a3 3 0 00-2.11-2.12C19.47 3.5 12 3.5 12 3.5s-7.47 0-9.39.57A3 3 0 00.5 6.19 31.4 31.4 0 000 12a31.4 31.4 0 00.5 5.81 3 3 0 002.11 2.12C4.53 20.5 12 20.5 12 20.5s7.47 0 9.39-.57a3 3 0 002.11-2.12A31.4 31.4 0 0024 12a31.4 31.4 0 00-.5-5.81zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"/></svg>
);

export default function ChatPage() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [conversationId, setConversationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [actionItems, setActionItems] = useState([]);
  const [previousConversations, setPreviousConversations] = useState([]);
  const [showPreviousChats, setShowPreviousChats] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState("");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTransparency, setShowTransparency] = useState(() => localStorage.getItem("equal_hide_transparency") !== "1");
  const [showDisclaimer, setShowDisclaimer] = useState(() => localStorage.getItem("equal_hide_disclaimer") !== "1");
  const messagesEndRef = useRef(null);
  const didInit = useRef(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const email = localStorage.getItem("email");

  const getAxiosConfig = () => {
    const config = { withCredentials: true };
    if (token) config.headers = { Authorization: `Bearer ${token}` };
    return config;
  };

  useEffect(() => {
    if (didInit.current) return; // guard against StrictMode double-invoke (prefill is one-shot)
    didInit.current = true;
    const hash = window.location.hash;
    // Prefill a suggested question when arriving from the Property Roadmap
    const prefill = localStorage.getItem("equal_chat_prefill");
    if (prefill) { setInputMessage(prefill); localStorage.removeItem("equal_chat_prefill"); }
    if (hash && hash.includes("session_id")) handleGoogleCallback();
    else loadConversationHistory(!!prefill);
    fetchSources();
    // Show onboarding for first-time users
    if (!localStorage.getItem("equal_onboarded")) {
      setTimeout(() => setShowOnboarding(true), 800);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const fetchSources = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/documents/sources`, getAxiosConfig());
      setSources(res.data.sources || []);
    } catch (_err) {
      // Suppress in production — sources are optional
    }
  }, []); // stable ref — API and axios are module-level constants

  const handleGoogleCallback = async () => {
    const params = new URLSearchParams(window.location.hash.substring(1));
    const sessionId = params.get("session_id");
    if (!sessionId) { loadConversationHistory(); return; }
    try {
      const response = await axios.post(`${API}/auth/google-callback?session_id=${sessionId}`);
      localStorage.setItem("email", response.data.user.email);
      localStorage.setItem("role", response.data.user.role);
      window.history.replaceState({}, document.title, "/chat");
      toast.success(`Welcome ${response.data.user.name || response.data.user.email}!`);
      loadConversationHistory();
    } catch (error) { toast.error("Authentication failed"); navigate("/"); }
  };

  const loadConversationHistory = async (forceNew = false) => {
    try {
      const res = await axios.get(`${API}/chat/history`, getAxiosConfig());
      const all = res.data.conversations;
      const active = all.filter(c => !c.session_ended);
      setPreviousConversations(all);
      // When arriving with a prefilled question (from the Roadmap), stay on a
      // fresh composer so the question is visible instead of the history overlay.
      if (forceNew) { /* keep welcome/composer visible */ }
      else if (active.length === 1) loadConversation(active[0].id);
      else if (all.length > 1) setShowPreviousChats(true);
    } catch (error) {
      // Suppress in production — history loads gracefully on next attempt
    } finally { setLoadingHistory(false); }
  };

  const loadConversation = async (convId) => {
    try {
      const res = await axios.get(`${API}/chat/conversation/${convId}`, getAxiosConfig());
      const conv = res.data.conversation;
      setConversationId(conv.id);
      setMessages(conv.messages);
      setSessionEnded(false);
      setActionItems(conv.action_items || []);
      setShowPreviousChats(false);
      toast.success("Conversation loaded");
    } catch (_err) { toast.error("Failed to load conversation"); }
  };

  const startNewConversation = () => {
    setMessages([]); setConversationId(null);
    setSessionEnded(false); setActionItems([]);
    setShowPreviousChats(false);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading || sessionEnded) return;
    const userMsg = { role: "user", content: inputMessage, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInputMessage("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat/message`,
        { message: inputMessage, conversation_id: conversationId, document_filter: selectedSource || null },
        getAxiosConfig()
      );
      const { conversation_id, message, sources: srcs } = res.data;
      setConversationId(conversation_id);
      setMessages(prev => [...prev, { ...message, sources: srcs || [] }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send message");
    } finally { setLoading(false); }
  };

  const handleFlagMessage = async (messageIndex) => {
    if (!conversationId) return;
    try {
      await axios.post(`${API}/chat/flag-message`,
        { conversation_id: conversationId, message_index: messageIndex, reason: "User requested human assistance" },
        getAxiosConfig()
      );
      toast.success("Flagged for human review");
      setMessages(prev => prev.map((m, i) => i === messageIndex ? { ...m, flagged_for_human: true } : m));
    } catch (_err) { toast.error("Failed to flag"); }
  };

  const handleEmailChat = async () => {
    if (!conversationId) return;
    try {
      toast.info("Emailing transcript...");
      const res = await axios.post(`${API}/chat/email-transcript`, { conversation_id: conversationId }, getAxiosConfig());
      toast.success(res.data.message || "Transcript sent!");
    } catch (_err) { toast.error("Failed to email"); }
  };

  const handleEndSession = async () => {
    if (!conversationId) return;
    try {
      const res = await axios.post(`${API}/chat/end-session`, { conversation_id: conversationId }, getAxiosConfig());
      toast.success("Session ended. City officials notified.");
      setSessionEnded(true);
      setActionItems(res.data.action_items || []);
    } catch (_err) { toast.error("Failed to end session"); }
  };

  return (
    <div className="h-screen flex flex-col bg-page">

      {/* Header */}
      <header className="flex items-center justify-between gap-4 px-5 md:px-7 h-14 bg-white border-b border-line">
        <Brand hint="Cannabis Equity Advisory" />

        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button onClick={() => navigate('/home')}
            className="btn-ghost btn-sm"
            data-testid="home-button">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1h3a1 1 0 001-1V10"/>
            </svg>
            Home
          </button>
          <button onClick={() => navigate('/roadmap')}
            className="btn-ghost btn-sm text-equal-dark"
            data-testid="roadmap-button">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
            </svg>
            Roadmap
          </button>
          <button onClick={() => navigate('/studio')}
            className="btn-primary btn-sm"
            data-testid="design-studio-button">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm0 6h16M10 4v16"/>
            </svg>
            Design Studio
          </button>
          <button onClick={() => navigate('/comps')}
            className="btn-ghost btn-sm"
            data-testid="market-data-button">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            </svg>
            Market Data
          </button>
          <button onClick={() => window.open('https://calendly.com/yantkach/cannabis', '_blank')}
            className="btn-ghost btn-sm text-equal-dark"
            data-testid="schedule-call-button">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
            Schedule a Call
          </button>

          {conversationId && messages.length > 0 && (
            <button onClick={handleEmailChat}
              className="btn-ghost btn-sm"
              data-testid="email-chat-button">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              Email Chat
            </button>
          )}

          {previousConversations.length > 0 && !showPreviousChats && (
            <button onClick={() => setShowPreviousChats(true)}
              className="btn-ghost btn-sm"
              data-testid="view-previous-chats-button">
              History
            </button>
          )}

          <div className="w-px h-4 bg-line" />
          <span className="hidden md:inline text-mute text-xs font-mono">{email}</span>
          <button onClick={() => { localStorage.clear(); navigate("/"); }}
            className="btn-quiet btn-sm"
            data-testid="logout-button">
            Sign out
          </button>
        </div>
      </header>

      {/* Transparency bar */}
      {showTransparency && (
        <div className="px-5 md:px-7 py-1.5 flex items-center justify-between text-xs bg-mint border-b border-[#cfe9db]"
          data-testid="transparency-reminder">
          <span className="font-mono text-[11px] tracking-wide text-equal-dark">This conversation is shared with City Officials for transparency</span>
          <div className="flex items-center gap-3">
            <span className="text-mute font-mono text-[11px]">{email}</span>
            <button
              onClick={() => { setShowTransparency(false); localStorage.setItem("equal_hide_transparency", "1"); }}
              className="text-equal-dark/60 hover:text-equal-dark transition-colors"
              aria-label="Dismiss transparency notice"
              data-testid="dismiss-transparency-button">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Disclaimer banner */}
      {showDisclaimer && (
        <div className="px-5 md:px-7 py-2 flex items-center gap-2 text-xs bg-warm border-b border-[#f3d9b0]">
          <svg className="w-3.5 h-3.5 text-[#c47a12] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <span className="text-[#7a4b0a] leading-relaxed flex-1">
            <strong>Informational purposes only.</strong> Equal AI provides general guidance — not legal, tax, financial, or architectural advice. Always consult a qualified professional before making decisions.
          </span>
          <button
            onClick={() => { setShowDisclaimer(false); localStorage.setItem("equal_hide_disclaimer", "1"); }}
            className="text-[#c47a12]/70 hover:text-[#c47a12] transition-colors flex-shrink-0"
            aria-label="Dismiss disclaimer"
            data-testid="dismiss-disclaimer-button">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="max-w-3xl mx-auto px-5 md:px-6 py-8 space-y-6">

            {/* Previous chats selector */}
            {showPreviousChats && (
              <div className="card-flat p-6" data-testid="previous-chats-selector">
                <Eyebrow className="mb-1">Sessions</Eyebrow>
                <h3 className="text-ink font-bold mb-4 text-lg tracking-tight">
                  Continue a session
                </h3>
                <div className="space-y-2 mb-5">
                  {previousConversations.map(conv => (
                    <div key={conv.id} onClick={() => loadConversation(conv.id)}
                      className="flex items-center justify-between p-3 rounded-[10px] cursor-pointer transition-colors hover:bg-mint border border-line"
                      data-testid={`prev-conv-${conv.id}`}>
                      <div>
                        <p className="text-ink text-xs font-medium">{new Date(conv.created_at).toLocaleString()}</p>
                        <p className="text-mute text-xs mt-0.5">{conv.messages?.length || 0} messages</p>
                      </div>
                      <span className="text-xs text-equal-dark font-medium flex items-center gap-1">
                        {conv.session_ended ? "View" : "Continue"}
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                        </svg>
                      </span>
                    </div>
                  ))}
                </div>
                <button onClick={startNewConversation}
                  className="btn-primary w-full"
                  data-testid="start-new-conversation-button">
                  Start New Session
                </button>
              </div>
            )}

            {/* Welcome state */}
            {!showPreviousChats && messages.length === 0 && !loadingHistory && (
              <div className="py-8 animate-rise-in" data-testid="welcome-message">
                <RoadmapNudge />
                <div className="mt-8 w-14 h-14 rounded-[12px] bg-white border border-line flex items-center justify-center">
                  <EqualMark size={32} />
                </div>
                <h2 className="mt-6 text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">
                  Welcome to Equal AI
                </h2>
                <p className="mt-3 text-mute text-base max-w-md leading-relaxed">
                  Your cannabis equity advisor. Ask anything or pick a topic below to get started.
                </p>

                {/* Starter prompts */}
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    "What should I know before negotiating a cannabis lease?",
                    "How do I find off-market properties in Long Beach?",
                    "What does a cannabis attorney do and do I need one?",
                    "What grants or funding are available to equity applicants?",
                    "What are the zoning requirements for a cannabis dispensary?",
                    "How do I approach a landlord about renting to a cannabis business?",
                  ].map((prompt, i) => (
                    <button key={i} onClick={() => setInputMessage(prompt)}
                      className="card-flat text-left px-4 py-3 text-sm text-ink leading-relaxed flex gap-3 hover:border-equal hover:-translate-y-px transition-[border-color,transform] duration-200"
                      style={{ animationDelay: `${i * 40}ms` }}>
                      <span className="font-mono text-equal">→</span>
                      <span>{prompt}</span>
                    </button>
                  ))}
                </div>

                {/* Design Studio entry */}
                <button onClick={() => navigate('/studio')}
                  className="btn-primary mt-8 h-11 px-5"
                  data-testid="welcome-design-studio-button">
                  Plan your build-out in the Design Studio
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                  </svg>
                </button>
              </div>
            )}

            {/* Messages */}
            {!showPreviousChats && messages.map((msg, idx) => (
              <div key={msg.timestamp || idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} message-enter`}>
                <div className={`max-w-2xl ${msg.role === "user" ? "items-end" : "items-start"} flex flex-col gap-1`}>
                  {/* Bubble */}
                  <div className={`px-5 py-3.5 text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "bg-equal text-white rounded-[14px] rounded-br-[4px]"
                      : "bg-white text-ink border border-line rounded-[14px] rounded-bl-[4px]"
                  }`}>
                    {msg.role === "user" ? (
                      <div className="whitespace-pre-wrap">{msg.content}</div>
                    ) : (
                      <div className="prose prose-sm max-w-none
                        prose-headings:font-semibold prose-headings:text-ink prose-headings:mt-3 prose-headings:mb-1
                        prose-h1:text-base prose-h2:text-sm prose-h3:text-sm
                        prose-p:text-ink prose-p:my-1.5 prose-p:leading-relaxed
                        prose-strong:text-ink prose-strong:font-semibold
                        prose-ul:my-2 prose-ul:space-y-1 prose-li:text-ink-2
                        prose-ol:my-2 prose-ol:space-y-1
                        prose-a:text-equal-dark prose-a:no-underline hover:prose-a:underline
                        prose-code:text-equal-dark prose-code:bg-mint prose-code:px-1 prose-code:rounded prose-code:text-xs
                        prose-hr:border-line prose-hr:my-3
                        [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </div>
                    )}

                    {/* Sources */}
                    {msg.sources && msg.sources.length > 0 && (
                      <div className="mt-3 pt-3 space-y-1.5 border-t border-line">
                        <Eyebrow>Sources</Eyebrow>
                        {msg.sources.map((s, i) => (
                          <div key={i} className="flex items-center gap-2 flex-wrap">
                            <span className="text-mute text-xs truncate max-w-[200px]">{typeof s === 'string' ? stripExt(s) : stripExt(s.filename)}</span>
                            {s.timestamp && s.youtube_link && (
                              <a href={s.youtube_link} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md font-mono bg-[#fdecea] text-[#c0392b] border border-[#f2c1bb] hover:bg-[#c0392b] hover:text-white transition-colors"
                                data-testid={`source-link-${i}`}>
                                <YouTubeIcon />
                                {s.timestamp}
                              </a>
                            )}
                            {s.youtube_link && !s.timestamp && (
                              <a href={s.youtube_link} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-[#fdecea] text-[#c0392b] border border-[#f2c1bb] hover:bg-[#c0392b] hover:text-white transition-colors"
                                data-testid={`source-link-${i}`}>
                                <YouTubeIcon />
                                Watch
                              </a>
                            )}
                            {s.timestamp && !s.youtube_link && (
                              <span className="text-[11px] px-2 py-0.5 rounded-md font-mono bg-mint text-equal-dark border border-[#bfe5d1]">
                                {s.timestamp}
                              </span>
                            )}
                            {s.page_number && (
                              <span className="text-[11px] px-2 py-0.5 rounded-md font-mono bg-cream text-ink-2 border border-[#e6dfd0]">
                                p.{s.page_number}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Admin response */}
                    {msg.admin_response && (
                      <div className="mt-3 rounded-[10px] p-3 bg-mint border border-[#cfe9db]">
                        <Eyebrow className="text-equal-dark mb-1">Support Response</Eyebrow>
                        <p className="text-sm text-ink whitespace-pre-wrap">{msg.admin_response}</p>
                        <p className="text-xs text-mute mt-1 font-mono">{new Date(msg.admin_response_at).toLocaleString()}</p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {msg.role === "assistant" && (
                    <div className="flex items-center gap-3 px-1 mt-1">
                      <span className="text-mute text-[11px] font-mono">{new Date(msg.timestamp).toLocaleTimeString()}</span>
                      {msg.flagged_for_human && !msg.admin_response ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-[#c47a12] bg-warm border border-[#f3d9b0] px-2.5 py-1 rounded-full" data-testid={`flagged-badge-${idx}`}>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"/></svg>
                          Flagged for help
                        </span>
                      ) : !msg.flagged_for_human && (
                        <button onClick={() => handleFlagMessage(idx)}
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-mute bg-white border border-line hover:border-[#f3d9b0] hover:text-[#c47a12] hover:bg-warm px-2.5 py-1 rounded-full transition-colors duration-200"
                          data-testid={`flag-button-${idx}`}>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"/></svg>
                          Flag for help
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Typing */}
            {!showPreviousChats && loading && (
              <div className="flex justify-start">
                <div className="px-5 py-4 rounded-[14px] rounded-bl-[4px] flex gap-1.5 bg-white border border-line">
                  {[0,1,2].map(i => (
                    <div key={i} className="w-1.5 h-1.5 rounded-full bg-equal animate-bounce"
                      style={{ animationDelay: `${i * 0.15}s` }} />
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </div>

      {/* Input area */}
      <div className="px-5 md:px-6 pb-6 pt-4 bg-white border-t border-line">
        <div className="max-w-3xl mx-auto space-y-3">

          {/* Session filter */}
          {sources.length > 0 && !sessionEnded && !showPreviousChats && (
            <div className="flex items-center gap-3" data-testid="session-filter-bar">
              <span className="eyebrow whitespace-nowrap">Focus:</span>
              <div className="relative flex-1 max-w-xs">
                <select value={selectedSource} onChange={e => setSelectedSource(e.target.value)}
                  className="input-flat h-8 pl-3 pr-7 text-xs appearance-none cursor-pointer text-ink-2"
                  data-testid="session-filter-select">
                  <option value="">All sessions</option>
                  {sources.map(s => (
                    <option key={s.id} value={s.id}>{stripExt(s.filename.length > 45 ? s.filename.slice(0,42) + "..." : s.filename)}</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
                  <svg className="w-3 h-3 text-mute" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                  </svg>
                </div>
              </div>
              {selectedSource && (
                <button onClick={() => setSelectedSource("")}
                  className="text-xs text-equal-dark hover:text-ink flex items-center gap-1 transition-colors"
                  data-testid="clear-session-filter">
                  Focused
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Session ended state */}
          {showPreviousChats ? null : sessionEnded ? (
            <div className="card-flat p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 bg-equal">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
                <p className="text-ink font-bold text-sm tracking-tight">
                  Session Complete — Your Next Steps
                </p>
              </div>
              {actionItems.length > 0 ? (
                <ol className="space-y-3 mb-5">
                  {actionItems.map((item, idx) => (
                    <li key={`action-${idx}`} className="flex items-start gap-3" data-testid={`action-item-${idx}`}>
                      <span className="mt-0.5"><StepNumber n={idx + 1} active /></span>
                      <span className="text-ink-2 text-sm flex-1 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ol>
              ) : <p className="text-mute text-sm mb-4">Generating your action items...</p>}
              <p className="text-mute text-xs mb-4 border-t border-line pt-3">
                City officials have been notified. Your transcript has been emailed to you.
              </p>
              <button onClick={() => window.location.reload()}
                className="btn-primary h-10 px-5"
                data-testid="start-new-session-button">
                Start New Session
              </button>
            </div>
          ) : (
            <>
              <form onSubmit={handleSendMessage} className="flex gap-3">
                <input
                  value={inputMessage}
                  onChange={e => setInputMessage(e.target.value)}
                  placeholder="Ask about licensing, zoning, lease negotiations, equity programs..."
                  disabled={loading || sessionEnded}
                  className="input-flat flex-1 h-12 px-5 rounded-[12px]"
                  data-testid="message-input"
                />
                <button type="submit"
                  disabled={loading || sessionEnded || !inputMessage.trim()}
                  className="btn-primary h-12 px-6 rounded-[12px] disabled:opacity-30"
                  data-testid="send-message-button">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                  </svg>
                  Send
                </button>
              </form>

              {conversationId && (
                <div className="flex justify-center">
                  <button onClick={handleEndSession}
                    className="text-xs text-mute hover:text-[#c0392b] transition-colors"
                    data-testid="end-session-button">
                    End session & notify city
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <SetPasswordModal open={showSetPassword} onClose={() => setShowSetPassword(false)} />

      {/* First-time onboarding modal */}
      {showOnboarding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-ink/70">
          <div className="w-full max-w-md card-flat rounded-2xl overflow-hidden animate-rise-in">
            <div className="px-8 pt-8 pb-6 flex items-center gap-3 border-b border-line">
              <EqualMark size={28} />
              <div>
                <p className="text-ink font-bold text-lg tracking-tight">Welcome to Equal</p>
                <p className="text-mute text-xs">Here's how to get the most out of your advisor</p>
              </div>
            </div>

            <div className="px-8 py-6 space-y-2 bg-page">
              {[
                { title: "Ask anything", body: "Licensing, leases, zoning, the equity program." },
                { title: "Watch the source", body: "Every answer links to the workshop video and timestamp." },
                { title: "Flag for help", body: "Stuck? Flag it and a human advisor replies." },
                { title: "End your session", body: "Get a transcript and your next steps." },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-[10px] bg-white border border-line">
                  <span className="mt-0.5"><StepNumber n={i + 1} active={i === 0} /></span>
                  <div>
                    <p className="text-sm font-semibold text-ink">{item.title}</p>
                    <p className="text-xs text-mute mt-0.5 leading-relaxed">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-8 py-6">
              <button
                onClick={() => { setShowOnboarding(false); localStorage.setItem("equal_onboarded", "1"); }}
                className="btn-primary w-full h-11"
                data-testid="onboarding-dismiss">
                Get Started
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
